<template>
  <div>
    <div class="card-container" v-if="data">
      <div class="scroller">
        <loading-component :value="loading" />
        <order-card :id="parseInt($route.params.id)" :data="data" />
        <logs-table
          :logs="filteredStatusesLogs"
          :color-coding="actionsColorCoding"
          title-key="status"
          color-coding-key="value"
          datetime-key="created_at"
          v-if="filteredStatusesLogs.length"
          @status-click="focusOnStatus"
        />
        
        <!-- Booking Complaints Section -->
        <v-card class="complaints-section" v-if="data">
          <v-card-title class="complaints-header">
            <span>Complaints</span>
            <v-spacer></v-spacer>
            <v-chip small color="warning" v-if="pendingComplaintsCount > 0">
              {{ pendingComplaintsCount }} Pending
            </v-chip>
          </v-card-title>
          
          <v-card-text>
            <!-- Add New Complaint -->
            <div class="add-complaint-form mb-4">
              <v-textarea
                v-model="newComplaintDescription"
                label="Add a complaint"
                placeholder="Enter complaint description..."
                rows="3"
                outlined
                dense
                hide-details
              ></v-textarea>
              <v-btn
                color="primary"
                small
                @click="createComplaint"
                :loading="creatingComplaint"
                :disabled="!newComplaintDescription || newComplaintDescription.trim() === ''"
                class="mt-2"
              >
                Add Complaint
              </v-btn>
            </div>
            
            <!-- Complaints List -->
            <div v-if="complaints.length > 0" class="complaints-list">
              <v-card
                v-for="complaint in complaints"
                :key="complaint.id"
                class="complaint-item mb-3"
                outlined
              >
                <v-card-text>
                  <div class="complaint-header-row">
                    <v-chip
                      :color="complaint.status === 'PENDING' ? 'warning' : 'success'"
                      small
                      text-color="white"
                    >
                      {{ complaint.status }}
                    </v-chip>
                    <span class="complaint-date">
                      Created: {{ formatDate(complaint.created_at) }}
                      <span v-if="complaint.creator">
                        by {{ complaint.creator.first_name }} {{ complaint.creator.last_name }}
                      </span>
                    </span>
                  </div>
                  
                  <p class="complaint-description mt-2 mb-2">{{ complaint.description }}</p>
                  
                  <!-- Resolved Info -->
                  <div v-if="complaint.status === 'RESOLVED'" class="resolved-info">
                    <div class="resolved-date mb-1">
                      <strong>Resolved:</strong> {{ formatDate(complaint.resolved_at) }}
                      <span v-if="complaint.resolver">
                        by {{ complaint.resolver.first_name }} {{ complaint.resolver.last_name }}
                      </span>
                    </div>
                    <div v-if="complaint.resolution_comment" class="resolution-comment">
                      <strong>Resolution:</strong> {{ complaint.resolution_comment }}
                    </div>
                  </div>
                  
                  <!-- Resolve Button for Pending -->
                  <v-btn
                    v-if="complaint.status === 'PENDING'"
                    color="success"
                    small
                    @click="openResolveDialog(complaint)"
                    class="mt-2"
                  >
                    Resolve
                  </v-btn>
                </v-card-text>
              </v-card>
            </div>
            
            <div v-else class="no-complaints text-center py-4">
              <p class="grey--text">No complaints for this booking</p>
            </div>
          </v-card-text>
        </v-card>
        
        <!-- Complaints and category section commented as requested
        <div class="complaints-container">
          <v-select
            label="Select"
            v-model="complaintSelectedVal"
            item-text="name"
            item-value="id"
            :items="complaintCategories"
            placeholder="Select a category"
          ></v-select>
          <button class="complaint-button" @click="saveCategory()">Save</button>
        </div>
        <logs-table
          :logs="data.all_complaints"
          title-key="description"
          datetime-key="created_at"
          doer-key="doer"
          captain-key="entity_id"
          :complaintStatus="complaintStatus"
          :addButton="true"
          @added="addComplaint"
          @showDetails="showDetails"
          @changeComplaintStatus="changeComplaintStatus"
        />
        -->
      </div>
    </div>
    <GmapMap :center="center" :options="options" :zoom="zoom" ref="map" class="map" v-if="data">
      <!-- Actual driven route -->
      <GmapPolyline
        v-if="path.length"
        :options="{ strokeColor: '#1C6758', strokeOpacity: 1.0, strokeWeight: 4 }"
        :path="path"
      ></GmapPolyline>
      <!-- Estimated route from summaries.estimated_route -->
      <GmapPolyline
        v-if="estimatedPath && estimatedPath.length"
        :options="{ strokeColor: '#000000', strokeOpacity: 0.7, strokeWeight: 4 }"
        :path="estimatedPath"
      ></GmapPolyline>
      <GmapMarker
        v-if="pickupPosition"
        :icon="{
        url: require('@/assets/map/pickup-pin.svg'),
        scaledSize: { width: 34, height: 42 },
      }"
        :position="pickupPosition"
        @mouseover="showByIndex = 'pickup'"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex == 'pickup'">
          <p style="margin-bottom: 0">Booking's pickup location</p>
        </gmap-info-window>
      </GmapMarker>
      <GmapMarker
        v-if="dropoffPosition"
        :icon="{
        url: require('@/assets/map/dropoff-pin.svg'),
        scaledSize: { width: 34, height: 42 },
      }"
        :position="dropoffPosition"
        @mouseover="showByIndex = 'dropoff'"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex == 'dropoff'">
          <p style="margin-bottom: 0">Booking's dropoff location</p>
        </gmap-info-window>
      </GmapMarker>
      <GmapMarker
        v-for="(marker, index) in statusMarkers"
        :key="`status-marker-${marker.id || index}-${marker.status}`"
        :icon="{
          url: formSvg(actionsColorCoding[marker.value]),
          scaledSize: { width: 50, height: 35 },
        }"
        :position="getMarkerPositionWithOffset(marker, index)"
        @mouseover="showByIndex = index"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex === index">
          <p style="margin-bottom: 0">
            {{ getStatusLabel(marker.value) }} at
            {{ formatCompanyTime(marker.created_at, "HH[h]mm") }}
          </p>
        </gmap-info-window>
      </GmapMarker>
      <GmapMarker :icon="{
        url: require('@/assets/markers/FREE.png'),
        scaledSize: { width: 34, height: 42 },
      }" v-if="startTracking" :position="{
        lat: driverLocation.lat,
        lng: driverLocation.lng,
      }" />
    </GmapMap>

    <!-- Resolve Complaint Dialog -->
    <v-dialog v-model="resolveDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Resolve Complaint</span>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="resolutionComment"
            label="Resolution Comment (Optional)"
            placeholder="Enter what was done to resolve this complaint..."
            rows="4"
            outlined
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="resolveDialog = false" :disabled="resolvingComplaint">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            @click="resolveComplaint"
            :loading="resolvingComplaint"
          >
            Resolve
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>

</template>
<script>
/* eslint-disable */

import OrderCard from "@/components/dispatching/OrderCard.vue";
import LogsTable from "@/components/dispatching/LogsTable.vue";
import AdditionalInfo from "@/components/dispatching/AdditionalInfo.vue";
import GmapPolyline from "vue2-google-maps/dist/components/polyline";
import socketEventsMixin from "@/mixins/socketEventsMixin";
import axios from "axios";
import api from "@/constants/api";
import LoadingComponent from "@/components/LoadingComponent";
import { formatCompanyTime } from "@/util/companyTime";

const options = {
  mapTypeId: "roadmap",
  disableDefaultUI: true,
  styles: [
    {
      featureType: "administrative",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#a3a4ae",
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "all",
      stylers: [
        {
          color: "#e6eaf0",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "labels.text",
      stylers: [
        {
          visibility: "simplified",
        },
        {
          gamma: "2",
        },
        {
          lightness: "3",
        },
        {
          color: "#778494",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "labels.icon",
      stylers: [
        {
          color: "#788E98",
        },
      ],
    },
    {
      featureType: "poi.attraction",
      elementType: "geometry.fill",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [
        {
          visibility: "on",
        },
        {
          color: "#ceead6",
        },
      ],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#39b65c",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [
        {
          saturation: "0",
        },
        {
          lightness: "35",
        },
        {
          visibility: "simplified",
        },
        {
          gamma: "1",
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#fde293",
        },
      ],
    },
    {
      featureType: "road.arterial",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
        {
          saturation: "-50",
        },
        {
          lightness: "17",
        },
      ],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [
        {
          color: "#8fbaff",
        },
        {
          visibility: "on",
        },
      ],
    },
  ],
};

function hasValidCoordinates(geo) {
  if (!geo || !Array.isArray(geo.coordinates) || geo.coordinates.length < 2) return false;
  const [lng, lat] = geo.coordinates;
  return lng !== null && lng !== undefined && lat !== null && lat !== undefined;
}

function toLatLng(geo) {
  if (!hasValidCoordinates(geo)) return null;
  const [lng, lat] = geo.coordinates;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
  return { lat: latNum, lng: lngNum };
}

// {
//   position: {
//     lat: 43.738359,
//     lng: 7.426723,
//   },
//   icon: {
//     url: require("@/assets/map/pickup-pin.svg"),
//     scaledSize: {
//       width: 34,
//       height: 42,
//     },
//   },
// },

function zoomUI(map) {
  // Container

  const zoomUIContainer = document.createElement("div");
  zoomUIContainer.classList = "zoom-container";

  // Zoom in

  const zoomIn = document.createElement("button");
  zoomIn.classList = "zoom-button";
  zoomIn.title = "Click to zoom in";

  const zoomInText = document.createElement("div");
  zoomInText.innerHTML = `+`;
  zoomIn.appendChild(zoomInText);

  zoomUIContainer.append(zoomIn);

  zoomIn.addEventListener("click", () => {
    map.setZoom(map.getZoom() + 1);
  });

  // Zoom out

  const zoomOut = document.createElement("button");
  zoomOut.classList = "zoom-button";
  zoomOut.title = "Click to zoom out";

  const zoomOutText = document.createElement("div");
  zoomOutText.innerHTML = `-`;
  zoomOut.appendChild(zoomOutText);

  zoomUIContainer.append(zoomOut);

  zoomOut.addEventListener("click", () => {
    map.setZoom(map.getZoom() - 1);
  });

  map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomUIContainer);
}

export default {
  components: {
    OrderCard,
    GmapPolyline,
    LogsTable,
    AdditionalInfo,
    LoadingComponent,
  },
  title: "Order # Details",
  mixins: [socketEventsMixin],
  events() {
    return {
      captain_location_updated: this.updateDriverLocation,
      [this.api.SOCKET.EVENTS.UPDATE_BOOKING_STATUS]: this.updateStatus,
    };
  },
  computed: {
    pickupPosition() {
      return this.data ? toLatLng(this.data.pick_up_location) : null;
    },
    dropoffPosition() {
      return this.data ? toLatLng(this.data.pull_down_location) : null;
    },
    statusMarkers() {
      if (!this.data || !Array.isArray(this.data.statuses)) return [];
      return this.data.statuses
        .filter((marker) => {
          if (!marker) return false;
          // Show ARRIVED and ACCEPTED on map, hide only APPROVED, PAID, STARTED
          if (["APPROVED", "PAID", "STARTED"].includes(marker.status))
            return false;
          return !!toLatLng(marker.location);
        })
        .map((marker) => {
          const position = toLatLng(marker.location);
          return {
            ...marker,
            _position: position,
            _originalPosition: position, // Keep original position
            value: marker.status, // Ensure value is set for color coding
          };
        });
    },
    startTracking() {
      return [
        !!this.data.now && "ACCEPTED",
        "STARTED",
        "ARRIVED",
        "PICKED_UP",
      ].includes(this.data.status);
    },
    drawRoute() {
      return ["PICKED_UP", "ARRIVED", "DONE", "PAID"].includes(
        this.data.status
      );
    },

    filteredStatusesLogs() {
      if (!this.data || !this.data.statuses) return [];
      return this.data.statuses.map((status) => {
        const item = { ...status };
        item.value = item.status;
        const labels = {
          PENDING: "Pending",
          ACCEPTED: "Accepted",
          STARTED: "Started",
          ARRIVED: "Arrived to pickup",
          PICKED_UP: "Trip Started",
          DONE: "Arrived to dropoff",
          PAID: "Paid",
          SYSTEM_CANCELLED: "Cancelled by system",
          PASSENGER_CANCELLED: "Cancelled by customer",
          CAPTAIN_CANCELLED: "Cancelled by driver",
          CC_CANCELLED: "Cancelled by CC",
        };
        item.status = labels[item.status] || item.status;
        return item;
      });
    },
    pendingComplaintsCount() {
      return this.complaints.filter(c => c.status === 'PENDING').length;
    },
  },
  data() {
    return {
      data: null,
      complaintSelectedVal: null,
      loading: null,
      complaintStatus: null,
      dialog: false,
      complaintCategories: [],
      center: { lat: 43.738359, lng: 7.426723 },
      zoom: 16,
      options: options,
      driverLocation: { lat: 0, lng: 0 },
      // Actual driven route from RideTracking
      path: [],
      // Estimated route decoded from summaries.estimated_route
      estimatedPath: [],
      showByIndex: null,
      actionsColorCoding: {
        APPROVED: "#293462",
        ACCEPTED: "#293462",
        STARTED: "#1CD6CE",
        ARRIVED: "#FEDB39",
        AT_DESTINATION: "#1CD6CE",
        PICKED_UP: "#F57429",
        DONE: "#1C6758",
        PAID: "#1C6758",
        CAPTAIN_CANCELLED: "#9B1FE8",
        SYSTEM_CANCELLED: "#9B1FE8",
        PAX_CANCELLED: "#9B1FE8",
        CANCELLED: "#9B1FE8",
        REJECTED: "#E10015",
      },
      complaint: "",
      // Booking Complaints
      complaints: [],
      newComplaintDescription: "",
      creatingComplaint: false,
      resolveDialog: false,
      selectedComplaint: null,
      resolutionComment: "",
      resolvingComplaint: false,
    };
  },
  methods: {
    changeComplaintStatus(value) {
      this.loading = true;
      let status = value;
      axios.put(`complaint/${this.$route.params.id}/update_status`, { status })
        .then(() => {
          this.loading = false;
          window.getApp.$emit("SHOW_TOAST", "Saved!");

        }).catch((err) => {
          window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
          this.loading = false;
        });
    },

    onlyUnique(value, index, self) {
      self = self.map((item) => item.status);
      return (
        self.indexOf(value.status) === index &&
        !["APPROVED", "DONE", "STARTED", "ARRIVED"].includes(value.status)
      );
    },
    getStatusesToShow(allStatuses) {
      const statuses = {};

      allStatuses.forEach(item => {
        const { status, created_at } = item;
        if ((!["APPROVED", "DONE", "STARTED", "ARRIVED"].includes(status)) && (!(status in statuses) || created_at > statuses[status].created_at)) {
          statuses[status] = item;
        }
      });

      return Object.values(statuses);

    },
    getOrderDetails() {
      axios
        .get(api.ACTIONS.BOOKINGS + "/" + this.$route.params.id)
        .then((res) => {
          this.data = res.data.data;
          // reset routes on each load
          this.path = [];
          this.estimatedPath = [];

          this.complaintStatus = this.data.meta.complaint_status;
          this.complaintSelectedVal = this.data.all_complaints.length > 0 ? this.data.all_complaints[0].category_id : null;
          document.title = `Order #${this.data.id} Details | Snap Drive`;
          
          // Fetch booking complaints
          this.fetchComplaints();

          // Fill estimated route from backend (summaries.estimated_route decoded)
          if (this.data.estimated_route && Array.isArray(this.data.estimated_route)) {
            this.estimatedPath = this.data.estimated_route.map((p) => ({
              lat: parseFloat(p.lat),
              lng: parseFloat(p.lng),
            }));
          }
          try {
            this.driverLocation.lat =
              this.data.captain_last_location[0].location.coordinates[1];
            this.driverLocation.lng =
              this.data.captain_last_location[0].location.coordinates[0];
          } catch (err) {
            // do nothing
          }
          if (
            [
              "PICKED_UP",
              "DONE",
              "PAID",
              "REJECTED",
              "CANCELLED",
              "CAPTAIN_CANCELLED",
              "PAX_CANCELLED",
            ].includes(res.data.data.status) &&
            this.data.ride_id
          ) {
            axios
              .get("ride/" + this.data.ride_id + "/route")
              .then((res) => {
                res.data.data.route.forEach((e) => {
                  let obj = {};
                  obj["lat"] = parseFloat(e.lat);
                  obj["lng"] = parseFloat(e.lng);
                  this.path.push(obj);
                });
                if (Array.isArray(this.data.statuses)) {
                  this.data.statuses.forEach(({ value, location }) => {
                    if (value !== "PICKED_UP") return;
                    const pos = toLatLng(location);
                    if (pos) this.path.unshift(pos);
                  });
                }
                if (this.drawRoute && this.startTracking) {
                  let lastPathPoint = this.path[this.path.length - 1];
                  if (lastPathPoint && lastPathPoint.lng != null && lastPathPoint.lat != null) {
                    this.updateDriverLocation({
                      location: {
                        coordinates: [lastPathPoint.lng, lastPathPoint.lat],
                      },
                      captain_id: this.data.ride.captain_id,
                    });
                  }
                }
                // console.log("Data route");
                // console.log(this.path);
              })
              .catch((err) => {
                console.log(err);
              });
          }
          window.requestAnimationFrame(() => {
            this.$refs.map.$mapPromise.then((map) => {
              zoomUI(map);
              const bounds = new google.maps.LatLngBounds();

              if (this.data.status === "PAID" || this.data.status === "DONE") {
                if (this.pickupPosition) bounds.extend(this.pickupPosition); // extend bounds to pickup marker
                if (this.dropoffPosition) bounds.extend(this.dropoffPosition);
              } else if (
                ![
                  "CANCELLED",
                  "CAPTAIN_CANCELLED",
                  "PAX_CANCELLED",
                  "SYSTEM_CANCELLED",
                ].includes(this.data.status)
              ) {
                if (this.pickupPosition) bounds.extend(this.pickupPosition); // extend bounds to pickup marker
              } else {
                const lastStatus = Array.isArray(this.data.statuses)
                  ? this.data.statuses[this.data.statuses.length - 1]
                  : null;
                const lastPos = lastStatus ? toLatLng(lastStatus.location) : null;
                if (lastPos) bounds.extend(lastPos);
                else if (this.pickupPosition) bounds.extend(this.pickupPosition);
                else if (this.dropoffPosition) bounds.extend(this.dropoffPosition);
                else if (this.driverLocation && this.driverLocation.lat && this.driverLocation.lng)
                  bounds.extend(this.driverLocation);
              }
              // Fit bounds only if we managed to add at least one point
              // (LatLngBounds throws in some cases if it's empty)
              try {
                map.fitBounds(bounds);
              } catch (e) {
                // ignore
              }
              let zoomLvl = map.getZoom();
              if (![
                "PAID",
                "DONE"
              ].includes(this.data.status)) {
                google.maps.event.addListenerOnce(map, 'idle', () => {
                  map.setZoom(this.zoom);
                });
              }

            });
          });
        })
        .catch((error) => {
          console.log("RidePage Error: ", error);
        });
    },
    updateDriverLocation(data) {
      const pos = data ? toLatLng(data.location) : null;
      if (!pos) return;
      if (
        this.data.ride &&
        data.captain_id == this.data.ride.captain_id &&
        this.startTracking
      ) {
        this.driverLocation.lat = pos.lat;
        this.driverLocation.lng = pos.lng;
        if (this.drawRoute)
          this.path.push(pos);
        window.requestAnimationFrame(() => {
          this.$refs.map.$mapPromise.then((map) => {
            let zoomLvl = map.getZoom();
            const bounds = new google.maps.LatLngBounds();
            bounds.extend({
              lat: this.driverLocation.lat,
              lng: this.driverLocation.lng,
            });
            map.fitBounds(bounds);
            map.setZoom(zoomLvl);
          });
        });
      }
    },
    formSvg(color) {
      return (
        "data:image/svg+xml;utf8," +
        encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="33.625" height="41.385" viewBox="0 0 33.625 41.385">
        <defs>
          <clipPath id="clip-path">
            <rect id="Rectangle_3619" data-name="Rectangle 3619" width="33.625" height="41.385" transform="translate(163 565)" fill="none"/>
          </clipPath>
        </defs>
        <g id="Mask_Group_6" data-name="Mask Group 6" transform="translate(-163 -565)" clip-path="url(#clip-path)">
          <g id="Monaco_Pin_Active" data-name="Monaco Pin Active" transform="translate(163.259 565)">
            <g id="Group_322" data-name="Group 322" transform="translate(6.622 22.284)">
              <path id="Path_374" data-name="Path 374" d="M42.018,34.462l-9.932,19.1-9.932-19.1a17.086,17.086,0,0,0,19.865,0Z" transform="translate(-22.154 -34.462)" fill="${color}"/>
            </g>
            <g id="Group_323" data-name="Group 323" transform="translate(0)">
              <circle id="Ellipse_3825" data-name="Ellipse 3825" cx="16" cy="16" r="16" transform="translate(0.741)" fill="${color}"/>
              <path id="Path_376" data-name="Path 376" d="M23.081,19.3a9.705,9.705,0,0,1-5.418-1.658l-.123-.118a8.642,8.642,0,0,1-3.694-7.1,9.242,9.242,0,0,1,18.47,0,8.642,8.642,0,0,1-3.694,7.1l-.123.118A9.705,9.705,0,0,1,23.081,19.3Z" transform="translate(-6.527 5.499)" fill="#fff"/>
            </g>
          </g>
        </g>
      </svg>`)
      );
    },
    getLogs() {
      axios
        .get(`${api.ACTIONS.LOGS}/${this.$route.params.id}`)
        .then((res) => {
          this.logs = res.data.data.data;
        })
        .catch((err) => { });
    },
    addComplaint() {
      this.$dialova({
        title: "Enter your complaint description",
        input: "Enter complaint",
        loader: true,
        fileUpload: "Sound files"
        // dropdown: {
        //   options: this.complaintCategories,
        //   placeholder: 'Select a category',
        //   name: 'complaint_type'
        // }
      }).then(({ value, close, files, dropdown }) => {
        const params = new FormData();
        if (value)
          params.append("description", value);
        params.append("booking_id", this.$route.params.id);
        //params.append("category_id", dropdown);
        if (files) {
          console.log(files.length);
          for (let i = 0; i < files.length; i++)
            params.append("wav_files[]", files[i]);
        }
        axios
          .post(`complaint/booking/${this.$route.params.id}/create`, params)
          .then(() => {
            window.getApp.$emit("SHOW_TOAST", "Saved!");
            this.getOrderDetails();
            close();
          })
          .catch((err) => {
            console.log("err");
            console.log(err.response.data.message);
            window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
            close();
          });
        // .finally(() => close());
      });
    },
    saveCategory() {
      if (!this.complaintSelectedVal) {
        window.getApp.$emit("SHOW_TOAST", "Please choose a category", "red");
        return;
      }
      this.loading = true;
      this.axios
        .post(`complaint/category`, {
          category_id: this.complaintSelectedVal,
          booking_id: this.$route.params.id
        })
        .then((res) => {
          this.loading = false;
        })
        .catch((err) => {
         // console.log(err.message);
          this.loading = false;
           window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
        });
    },
    showDetails(complaint, id) {
      const audioPromise = axios.get(`complaint/${id}/audio`);
      const commentPromise = axios.get(`complaint_comments/${id}/get_comments`);
      Promise.allSettled([audioPromise, commentPromise]).then(([audios, comments]) => {
        complaint.audios = audios.value.data.data;
        complaint.comments = comments.value.data.data;
        complaint.comments.forEach(comment => {
          comment.audios = comment.audios.map((item) => {
            return {
              item: item,
              loading: false,
              urlObject: ''
            }
          });
        });
        // this.$complaintDialog({
        //   complaint: complaint,
        //   hideCancel: true,
        //   fileUpload: "Sound files",
        //   list: audios.value.data.data,
        //   additionalData: {
        //     id: id
        //   }
        // })
      });

      // axios.get(`complaint/${id}/audio`).then((res) => {
      //   this.$complaintDialog({
      //     complaint:complaint,
      //     hideCancel: true,
      //     list:audios,
      //     additionalData: {
      //       id: id
      //     }
      //   })
      //     .then(() => {})
      //     .catch(() => {});
      // });
    },
    updateStatus(id, newStatus, oldStatus, driver, rideId) {
      if (id === this.data.id) {
        this.data.status = newStatus;
        if (driver) {
          this.data["captain_first_name"] = driver.first_name;
          this.data["captain_last_name"] = driver.last_name;
          this.data["taxi_number"] = driver.username;
          this.data['car_brand_model'] = driver.car_brand_model;
          this.data['car_brand'] = driver.car_brand;
          this.data['car_color'] = driver.car_color;
          this.data['car_plate_number'] = driver.car_plate_number;
          this.data["ride"] = {};
          this.data.ride["captain_id"] = driver.id;
        }
        if (rideId) {
          this.data["ride_id"] = rideId;
        }
        axios.get(`booking/${id}/statuses`).then((res) => {
          this.data.statuses = res.data.data;
        });
      }
    },
    focusOnStatus(log) {
      if (!log.location) return;
      const position = toLatLng(log.location);
      if (!position) return;
      
      window.requestAnimationFrame(() => {
        this.$refs.map.$mapPromise.then((map) => {
          map.setCenter(position);
          map.setZoom(18);
        });
      });
    },
    getMarkerPositionWithOffset(marker, index) {
      const basePosition = marker._originalPosition || marker._position;
      if (!basePosition) return marker._position;
      
      // Check if there are other markers at the same location
      const sameLocationMarkers = this.statusMarkers.filter((m, idx) => {
        if (!m._originalPosition) return false;
        const origPos = m._originalPosition;
        const distance = Math.sqrt(
          Math.pow(origPos.lat - basePosition.lat, 2) + 
          Math.pow(origPos.lng - basePosition.lng, 2)
        );
        return distance < 0.0001; // Very close markers (same location)
      });
      
      // Only add offset if there are multiple markers at same location
      if (sameLocationMarkers.length <= 1) {
        return basePosition;
      }
      
      // Find the index of this marker among same-location markers
      let sameLocationIndex = -1;
      for (let i = 0; i < sameLocationMarkers.length; i++) {
        const m = sameLocationMarkers[i];
        if (m._originalPosition && 
            Math.abs(m._originalPosition.lat - basePosition.lat) < 0.0001 &&
            Math.abs(m._originalPosition.lng - basePosition.lng) < 0.0001) {
          // Check if it's the same marker by comparing status and created_at
          if (m.status === marker.status && m.created_at === marker.created_at) {
            sameLocationIndex = i;
            break;
          }
        }
      }
      
      // If not found by exact match, use index in statusMarkers array
      if (sameLocationIndex === -1) {
        sameLocationIndex = sameLocationMarkers.findIndex(m => 
          m._originalPosition && 
          Math.abs(m._originalPosition.lat - basePosition.lat) < 0.0001 &&
          Math.abs(m._originalPosition.lng - basePosition.lng) < 0.0001
        );
      }
      
      // Calculate offset using golden angle spiral for even distribution
      const offset = 0.0001; // Increased offset in degrees (~10 meters)
      const goldenAngle = 137.508; // Golden angle in degrees
      const angle = (sameLocationIndex * goldenAngle) % 360;
      const radius = sameLocationIndex * offset * 0.5;
      
      return {
        lat: basePosition.lat + radius * Math.cos(angle * Math.PI / 180),
        lng: basePosition.lng + radius * Math.sin(angle * Math.PI / 180),
      };
    },
    getStatusLabel(status) {
      const labels = {
        PENDING: "Pending",
        ACCEPTED: "Accepted",
        STARTED: "Started",
        ARRIVED: "Arrived to pickup",
        PICKED_UP: "Trip Started",
        DONE: "Arrived to dropoff",
        PAID: "Paid",
        SYSTEM_CANCELLED: "Cancelled by system",
        PASSENGER_CANCELLED: "Cancelled by customer",
        CAPTAIN_CANCELLED: "Cancelled by driver",
        CC_CANCELLED: "Cancelled by CC",
      };
      return labels[status] || status;
    },
    // Booking Complaints Methods
    fetchComplaints() {
      if (!this.data || !this.data.id) return;
      
      axios
        .get(`${api.ACTIONS.BOOKINGS}/${this.data.id}/complaints`)
        .then((res) => {
          this.complaints = res.data.data.data || [];
        })
        .catch((err) => {
          console.error("Error fetching complaints:", err);
          window.getApp.$emit("SHOW_TOAST", "Failed to load complaints", "red");
        });
    },
    createComplaint() {
      if (!this.newComplaintDescription || this.newComplaintDescription.trim() === '') {
        window.getApp.$emit("SHOW_TOAST", "Please enter a complaint description", "red");
        return;
      }
      
      if (!this.data || !this.data.id) return;
      
      this.creatingComplaint = true;
      
      axios
        .post(`${api.ACTIONS.BOOKINGS}/${this.data.id}/complaints`, {
          description: this.newComplaintDescription.trim()
        })
        .then((res) => {
          window.getApp.$emit("SHOW_TOAST", "Complaint created successfully");
          this.newComplaintDescription = "";
          this.fetchComplaints();
        })
        .catch((err) => {
          console.error("Error creating complaint:", err);
          const message = (err.response && err.response.data && err.response.data.message) || "Failed to create complaint";
          window.getApp.$emit("SHOW_TOAST", message, "red");
        })
        .finally(() => {
          this.creatingComplaint = false;
        });
    },
    openResolveDialog(complaint) {
      this.selectedComplaint = complaint;
      this.resolutionComment = "";
      this.resolveDialog = true;
    },
    resolveComplaint() {
      if (!this.selectedComplaint) return;
      
      this.resolvingComplaint = true;
      
      axios
        .put(`booking-complaints/${this.selectedComplaint.id}/resolve`, {
          resolution_comment: this.resolutionComment.trim() || null
        })
        .then((res) => {
          window.getApp.$emit("SHOW_TOAST", "Complaint resolved successfully");
          this.resolveDialog = false;
          this.selectedComplaint = null;
          this.resolutionComment = "";
          this.fetchComplaints();
        })
        .catch((err) => {
          console.error("Error resolving complaint:", err);
          const message = (err.response && err.response.data && err.response.data.message) || "Failed to resolve complaint";
          window.getApp.$emit("SHOW_TOAST", message, "red");
        })
        .finally(() => {
          this.resolvingComplaint = false;
        });
    },
    formatDate(date) {
      if (!date) return "-";
      // Final-version doc 2026-10-05: Damascus time, whatever the PC zone.
      return formatCompanyTime(date, "DD/MM/YYYY HH:mm");
    },
  },
  created() {
    this.getOrderDetails();
  // this.getLogs(); // Disabled temporarily (no need to hit /api/user/log/{id} for now)
    // console.log(this.$store.getters.getBookingsUpdates);
    // this.$store.dispatch("listenBookingUpdate", this.$route.params.id || 'x');
  },
  mounted() {
  // Temporarily disable complaints categories fetching (no need to hit /api/user/complaint-category for now)
  // this.loading = true;
  // this.$store
  //   .dispatch("fetchComplaintCategories")
  //   .then((data) => {
  //     this.complaintCategories = data;
  //     this.categoryDialog = true;
  //     this.loading = false;
  //   })
  //   .catch((err) => {
  //     console.error(err);
  //     this.loading = false;
  //   });
  }
};
</script>

<style lang="scss">
.complaint-button {
  padding: 5px;
  width: 100%;
  background: #27a432;
  color: white;
}

.complaints-container {
  width: 100%;
  height: auto;
  float: left;
  padding: 1rem 1.25rem;
  background: white;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0px 0px 6px rgba($color: #000000, $alpha: 0.08);
  overflow-y: overlay;
}

.complaints-section {
  width: 100%;
  margin-top: 12px;
  
  .complaints-header {
    background-color: #f5f5f5;
    padding: 12px 16px;
  }
  
  .add-complaint-form {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 16px;
  }
  
  .complaints-list {
    max-height: 400px;
    overflow-y: auto;
  }
  
  .complaint-item {
    .complaint-header-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
    }
    
    .complaint-date {
      font-size: 12px;
      color: #666;
    }
    
    .complaint-description {
      color: #333;
      white-space: pre-wrap;
      word-break: break-word;
    }
    
    .resolved-info {
      background-color: #f0f8f0;
      padding: 12px;
      border-radius: 4px;
      margin-top: 8px;
      
      .resolved-date {
        font-size: 12px;
        color: #666;
      }
      
      .resolution-comment {
        margin-top: 8px;
        font-size: 13px;
        color: #333;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }
  
  .no-complaints {
    color: #999;
  }
}

.card-container {
  width: 430px;
  height: auto;
  position: absolute;
  z-index: 4;
  bottom: 0px;
  left: 55px;
  max-height: calc(100vh - 64px);
  padding-top: 22px;
  padding-bottom: 22px;
  overflow-y: overlay;

  &::-webkit-scrollbar {
    width: 0;
  }

  .scroller {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    flex-direction: column;
    gap: 12px;

    div {
      flex-grow: 0;
      flex-shrink: 1;
    }
  }
}

.map {
  height: calc(100vh - 64px);
  outline: none;
}

.zoom-container {
  width: max-content;
  height: max-content;
  bottom: 20px !important;
  right: 55px !important;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.zoom-button {
  height: 48px;
  width: 48px;
  background-color: #fff;
  border: 1px solid #c1c5d8;
  color: #222629;
  font-size: 18px;
  border-radius: 10px;
  outline: none;
}

.geofence-logs {
  color: #222629;

  tr,
  td,
  th {
    padding: 2px;
  }

  tr {
    td:first-child {
      padding-left: 0;
    }
  }
}
</style>
